import type { TagProcessType, TagType } from "@/types/entities/tags";
import {
  Avatar,
  AvatarFallback,
  Badge,
  Checkbox,
  Icon,
} from "@elaniintc/goes-design-system";
import { useCallback } from "react";
import { InputCounter } from "./InputCounter";
import type {
  SuspensionConfig,
  ProcessConfig,
} from "../hooks/useSuspensionState";
import { cn } from "@/utils/theme";

interface TagProcessComponentProps {
  tag: TagType;
  config: SuspensionConfig;
  onUpdateMaxDays: (id: string, maxDays: number) => void;
  onToggleGeneralConfig?: (tagId: string, enabled: boolean) => void;
  processesConfig: ProcessConfig[];
}

export const TagProcessComponent = ({
  tag,
  config,
  onUpdateMaxDays,
  onToggleGeneralConfig,
  processesConfig,
}: TagProcessComponentProps) => {
  const handleCheckboxChange = useCallback(
    (checked: boolean) => {
      onToggleGeneralConfig?.(tag.id, checked);
    },
    [tag.id, onToggleGeneralConfig],
  );

  const handleCounterChange = useCallback(
    (value: number) => {
      onUpdateMaxDays(config.id, value);
    },
    [config.id, onUpdateMaxDays],
  );

  if (config.type !== "tag") return null;

  const { maxDays, useGeneralConfig } = config;
  const checkboxId = `general-config-${tag.id}`;
  const daysBadgeVariant = maxDays > 0 ? "success" : "warning";
  const badgeVariant = useGeneralConfig ? daysBadgeVariant : "neutral";

  return (
    <div className="py-4 border-b border-neutral-quiet">
      <div className="flex flex-col md:flex-row justify-between">
        <div className="flex gap-2 items-center">
          <Avatar size="xs" shape="square">
            <AvatarFallback color="action" shape="square">
              <Icon
                type="fas"
                name="tag"
                className="text-action-quiet"
                aria-hidden="true"
              />
            </AvatarFallback>
          </Avatar>
          <Badge variant="neutral" size="default">
            {tag.name}
          </Badge>
        </div>

        <div className="flex gap-8 items-center justify-around md:justify-start">
          <div className="flex items-center gap-1">
            <label
              htmlFor={checkboxId}
              className="cursor-pointer text-body text-sm font-normal leading-3xl"
            >
              Configuración general
            </label>
            <Checkbox
              id={checkboxId}
              className="cursor-pointer"
              checked={useGeneralConfig}
              onChange={(e) => handleCheckboxChange(e.target.checked)}
              aria-label="Usar configuración general para todos los procesos"
            />
          </div>
          <InputCounter
            label="Plazo máximo (días)"
            value={maxDays}
            onChange={handleCounterChange}
            min={0}
            max={90}
            disabled={!useGeneralConfig}
          />
          <Badge
            variant={badgeVariant}
            size="default"
            className={cn(!useGeneralConfig && "text-on-disabled")}
          >
            {maxDays > 0 ? "Permitidas" : "Prohibidas"}
          </Badge>
        </div>
      </div>

      {!useGeneralConfig &&
        tag?.processes &&
        tag.processes.length > 0 &&
        tag.processes.map((process) => {
          const processConfig = processesConfig.find(
            (p) => p.id === process.id,
          );
          if (!processConfig) return null;

          return (
            <ProcessRow
              key={process.id}
              process={process}
              config={processConfig}
              onUpdateMaxDays={onUpdateMaxDays}
              isDisabled={useGeneralConfig}
            />
          );
        })}
    </div>
  );
};

interface ProcessRowProps {
  process: TagProcessType;
  config: ProcessConfig;
  onUpdateMaxDays: (id: string, maxDays: number) => void;
  isDisabled: boolean;
}

const ProcessRow = ({
  process,
  config,
  onUpdateMaxDays,
  isDisabled,
}: ProcessRowProps) => {
  const handleCounterChange = useCallback(
    (value: number) => {
      onUpdateMaxDays(config.id, value);
    },
    [config.id, onUpdateMaxDays],
  );
  const { maxDays } = config;
  const daysBadgeVariant = maxDays > 0 ? "success" : "warning";
  const badgeVariant = isDisabled ? "neutral" : daysBadgeVariant;

  return (
    <div className="py-4">
      <div className="flex flex-col md:flex-row justify-between">
        <div className="flex gap-2 items-center">
          <Avatar size="xs" shape="square">
            <AvatarFallback color="info" shape="square">
              <Icon
                type="fas"
                name="diagram-project"
                className="text-info-quiet"
                aria-hidden="true"
              />
            </AvatarFallback>
          </Avatar>
          <p className="font-semibold leading-3xl text-body-loud">
            {process.name}
          </p>
        </div>

        <div className="flex gap-8 items-center justify-around md:justify-start">
          <InputCounter
            label="Plazo máximo (días)"
            value={maxDays}
            onChange={handleCounterChange}
            min={0}
            max={90}
            disabled={isDisabled}
          />
          <Badge
            variant={badgeVariant}
            size="default"
            className={cn(isDisabled && "text-on-disabled")}
          >
            {maxDays > 0 ? "Permitidas" : "Prohibidas"}
          </Badge>
        </div>
      </div>
    </div>
  );
};
